import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiSearch } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getProducts, updateProduct, deleteProduct } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import VendorLayout from '../../components/layout/VendorLayout';
import './VendorProducts.css';

const picsum = (seed) => `https://picsum.photos/seed/${seed}/48/48`;


const STATUS_COLOR = { active: 'badge-success', inactive: 'badge-warning', pending: 'badge-info', rejected: 'badge-danger' };

export default function VendorProducts() {
  const { vendor } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (vendor?._id) params.vendor = vendor._id;
      const { data } = await getProducts(params);
      setProducts(data.products);
    } catch (err) {
      console.error('Vendor products error:', err?.response?.data || err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [vendor]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleToggleStatus = async (product) => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active';
    setToggling(product._id);
    try {
      const fd = new FormData();
      fd.append('status', newStatus);
      await updateProduct(product._id, fd);
      setProducts((prev) => prev.map((p) => p._id === product._id ? { ...p, status: newStatus } : p));
      toast.success(`Product ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    setDeleting(product._id);
    try {
      await deleteProduct(product._id);
      setProducts((prev) => prev.filter((p) => p._id !== product._id));
      toast.success('Product deleted');
    } catch {
      toast.error('Failed to delete product');
    } finally {
      setDeleting(null);
    }
  };

  const filtered = products.filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <VendorLayout
      title="My Products"
      action={
        <Link to="/vendor/products/add" className="btn btn-primary">
          <FiPlus size={15} /> Add Product
        </Link>
      }
    >
      <div className="vproducts-page">
        {/* Search toolbar */}
        <div className="vproducts-toolbar">
          <div className="vp-search">
            <FiSearch size={14} className="vp-search-icon" />
            <input
              className="vp-search-input"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span className="text-muted text-sm">{filtered.length} product{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /><span>Loading products…</span></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h3>{search ? 'No products match your search' : 'No products yet'}</h3>
            {!search && <p>Start by adding your first product.</p>}
            {!search && <Link to="/vendor/products/add" className="btn btn-primary"><FiPlus size={14} /> Add Product</Link>}
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Price</th>
                  <th style={{ textAlign: 'center' }}>Stock</th>
                  <th style={{ textAlign: 'center' }}>Reviews</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Active</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((product) => {
                  const imgSrc = product.images?.[0] || picsum(product._id);
                  const isLowStock = product.stock > 0 && product.stock < 10;
                  const statusCls = STATUS_COLOR[product.status] || 'badge-info';

                  return (
                    <tr key={product._id}>
                      {/* Product */}
                      <td>
                        <div className="vp-product-cell">
                          <img
                            src={imgSrc}
                            alt={product.name}
                            className="vp-thumb"
                            onError={(e) => { e.target.src = picsum(product.name); }}
                          />
                          <div>
                            <p className="vp-product-name">{product.name}</p>
                            {product.sku && <p className="vp-sku">SKU: {product.sku}</p>}
                          </div>
                        </div>
                      </td>

                      {/* Price */}
                      <td>
                        <p className="vp-price">Rs. {Number(product.price).toFixed(2)}</p>
                        {product.salePrice && (
                          <p className="vp-sale-price">Sale: Rs. {Number(product.salePrice).toFixed(2)}</p>
                        )}
                      </td>

                      {/* Stock */}
                      <td style={{ textAlign: 'center' }}>
                        <span className={`vp-stock ${product.stock === 0 ? 'out' : isLowStock ? 'low' : 'ok'}`}>
                          {product.stock}
                        </span>
                      </td>

                      {/* Reviews */}
                      <td style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem' }}>
                        ★ {Number(product.rating || 0).toFixed(1)} ({product.totalReviews || 0})
                      </td>

                      {/* Status */}
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${statusCls}`}>{product.status}</span>
                      </td>

                      {/* Toggle */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="vp-toggle-btn"
                          onClick={() => handleToggleStatus(product)}
                          disabled={toggling === product._id || product.status === 'pending' || product.status === 'rejected'}
                          title={product.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {product.status === 'active'
                            ? <FiToggleRight size={22} className="toggle-on" />
                            : <FiToggleLeft size={22} className="toggle-off" />}
                        </button>
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: 'right' }}>
                        <div className="vp-actions">
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate(`/vendor/products/edit/${product._id}`)}
                          >
                            <FiEdit2 size={13} />
                          </button>
                          <button
                            className="btn btn-sm vp-delete-btn"
                            onClick={() => handleDelete(product)}
                            disabled={deleting === product._id}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </VendorLayout>
  );
}
